import { useState, type FormEvent } from 'react';
import { cva } from 'class-variance-authority';
import { Flex, Input, Text } from '@nula/core-ui';

import { ApiError } from '@/lib/http/client';
import { isValidEmailShape } from '../utils/is-valid-email-shape';
import styles from './add-attendee-input.module.css';

export type AddAttendeeInputProps = {
  /**
   * Called with the trimmed email when the user submits a valid address.
   * Should resolve on success and reject (with an `ApiError` or `Error`) on
   * failure. Rejection message is surfaced inline below the input.
   */
  onAdd: (email: string) => Promise<unknown>;
};

const form = cva(styles.form);

/**
 * Footer input on `AttendeesSidePanel`. Submit happens on Enter (no button
 * — the input is the whole surface). A subtle `↵` hint pinned inside the
 * right edge of the input signals the affordance when the input has
 * content. Validates email shape locally before calling `onAdd` so we
 * don't round-trip obvious typos. Surfaces localized error messages for
 * common server codes:
 *   - `already_attendee` → "Already added to this meeting"
 *   - `invalid_email`    → "Enter a valid email"
 *   - `no_meeting_context` → "This doc isn't a meeting yet"
 *   - other → the underlying message string (or generic fallback)
 *
 * Clears the input on success. Disables the input while in-flight.
 */
export function AddAttendeeInput({ onAdd }: AddAttendeeInputProps) {
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (pending) return;
    const trimmed = value.trim();
    if (trimmed === '') return;
    if (!isValidEmailShape(trimmed)) {
      setError('Enter a valid email');
      return;
    }
    setError(null);
    setPending(true);
    try {
      await onAdd(trimmed);
      setValue('');
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setPending(false);
    }
  };

  const showEnterHint = value.trim().length > 0 && !pending;

  return (
    <form onSubmit={(e) => void handleSubmit(e)} className={form()}>
      <Flex vertical gap="xs">
        <div className={styles.inputWrap}>
          <Input
            aria-label="Add attendee by email"
            placeholder="Add attendee by email…"
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              if (error) setError(null);
            }}
            disabled={pending}
          />
          {showEnterHint ? (
            <Text
              size="xs"
              aria-hidden="true"
              data-testid="add-attendee-enter-hint"
              className={styles.enterHint}
            >
              ↵
            </Text>
          ) : null}
        </div>
        {error ? (
          <Text size="xs" className={styles.errorText} role="alert">{error}</Text>
        ) : null}
      </Flex>
    </form>
  );
}

function messageFor(err: unknown): string {
  if (err instanceof ApiError) {
    switch (err.code) {
      case 'already_attendee': return 'Already added to this meeting';
      case 'invalid_email': return 'Enter a valid email';
      case 'no_meeting_context': return "This doc isn't a meeting yet";
      default: return err.message || 'Could not add attendee';
    }
  }
  if (err instanceof Error) return err.message;
  return 'Could not add attendee';
}
